import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { clientsApi, contactsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search, ArrowRight, Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useCan } from "@/lib/rbac";

const ADDR_FIELDS = [
  { k: "address_line1", label: "Address line 1" },
  { k: "address_line2", label: "Address line 2" },
  { k: "zipcode", label: "Zip code" },
  { k: "city", label: "City" },
  { k: "country", label: "Country" },
];

function ContactCombobox({ contacts, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = contacts.find((c) => c.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="client-input-contact"
          className="mt-1.5 w-full flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white hover:bg-zinc-50 focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
        >
          <span className={cn(!selected && "text-zinc-400")}>
            {selected ? (
              <>
                <span className="font-medium">{selected.name}</span>
                <span className="text-xs text-zinc-500 ml-2 font-mono">{selected.email}</span>
              </>
            ) : (
              "Select a contact…"
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-zinc-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search contacts…" />
          <CommandList>
            <CommandEmpty>No contacts found. Create one in the Contacts page.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check className={cn("w-3.5 h-3.5 mr-2", !value ? "opacity-100" : "opacity-0")} />
                <span className="text-zinc-500 italic">No primary contact</span>
              </CommandItem>
              {contacts.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.email} ${c.phone}`}
                  onSelect={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("w-3.5 h-3.5 mr-2", value === c.id ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-xs text-zinc-500 font-mono">{c.email}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ClientForm({ initial, contacts, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    initial || {
      name: "",
      address_line1: "",
      address_line2: "",
      zipcode: "",
      city: "",
      country: "",
      primary_contact_id: null,
    }
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.name.trim()) {
          toast.error("Name is required");
          return;
        }
        onSubmit(form);
      }}
      className="space-y-3"
      data-testid="client-form"
    >
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Name</span>
        <input
          data-testid="client-input-name"
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </label>

      <div className="border-t border-zinc-100 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Address</div>
        {ADDR_FIELDS.map(({ k, label }) => (
          <label key={k} className="block mb-2">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
            <input
              data-testid={`client-input-${k}`}
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
              value={form[k] || ""}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
          </label>
        ))}
      </div>

      <div className="border-t border-zinc-100 pt-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Primary contact</span>
          <ContactCombobox
            contacts={contacts}
            value={form.primary_contact_id}
            onChange={(id) => setForm({ ...form, primary_contact_id: id })}
          />
          <span className="text-[11px] text-zinc-500 mt-1 block">
            Contacts must be created first in the <span className="font-semibold">Contacts</span> page.
          </span>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">
          Cancel
        </button>
        <button data-testid="client-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">
          Save
        </button>
      </div>
    </form>
  );
}

export default function Clients() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const canWrite = useCan("write:clients");
  const canReadContacts = useCan("read:contacts");

  const { data = [] } = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  // Operators can view clients but not contacts (403 forbidden). Gate the
  // query so we don't fire a call that produces a toast on page load.
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => contactsApi.list(),
    enabled: canReadContacts,
  });

  const contactById = Object.fromEntries(contacts.map((c) => [c.id, c]));

  const filtered = data.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const primary = contactById[c.primary_contact_id];
    return (
      (c.name || "").toLowerCase().includes(s) ||
      (c.city || "").toLowerCase().includes(s) ||
      (c.country || "").toLowerCase().includes(s) ||
      (primary?.email || "").toLowerCase().includes(s)
    );
  });
  const pager = usePager(filtered, { defaultSize: 50, sizes: [25, 50, 100] });

  const inval = () => qc.invalidateQueries({ queryKey: ["clients"] });
  const createM = useMutation({ mutationFn: clientsApi.create, onSuccess: () => { toast.success("Client created"); inval(); setModal(null); } });
  const updateM = useMutation({ mutationFn: ({ id, data }) => clientsApi.update(id, data), onSuccess: () => { toast.success("Client updated"); inval(); setModal(null); } });
  const removeM = useMutation({ mutationFn: clientsApi.remove, onSuccess: () => { toast.success("Client deleted"); inval(); } });

  return (
    <PageContainer>
      <PageHeader
        title="Clients"
        subtitle="Organizations that own devices, gateways and webhook destinations."
        actions={
          canWrite ? (
            <button
              data-testid="client-new"
              onClick={() => setModal({ mode: "create" })}
              className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> New client
            </button>
          ) : null
        }
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
            <input
              data-testid="client-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              placeholder="Search name, city, country or email…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
            />
          </div>
          <div className="text-xs text-zinc-500 font-mono">{filtered.length} total</div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-xs uppercase tracking-wider text-zinc-500">
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Primary contact</th>
              <th className="text-left p-3 font-semibold">Location</th>
              <th className="text-left p-3 font-semibold">Created</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {pager.pageRows.map((c) => {
              const primary = contactById[c.primary_contact_id];
              const location = [c.city, c.country].filter(Boolean).join(", ");
              return (
                <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`client-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 text-zinc-600">
                    {primary ? (
                      <>
                        <div className="text-sm">{primary.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{primary.email}</div>
                      </>
                    ) : (
                      <span className="text-xs text-zinc-400 italic">— none —</span>
                    )}
                  </td>
                  <td className="p-3 text-zinc-600 text-sm">{location || "—"}</td>
                  <td className="p-3 text-xs text-zinc-500 font-mono">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`client-open-${c.id}`}
                        onClick={() => navigate(`/clients/${c.id}`)}
                        className="text-zinc-600 hover:text-zinc-900 p-1.5 rounded hover:bg-zinc-100"
                        title="Open"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      {canWrite && (
                        <>
                          <button
                            data-testid={`client-edit-${c.id}`}
                            onClick={() => setModal({ mode: "edit", row: c })}
                            className="text-zinc-600 hover:text-zinc-900 p-1.5 rounded hover:bg-zinc-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`client-delete-${c.id}`}
                            onClick={() => { if (window.confirm(`Delete "${c.name}"?`)) removeM.mutate(c.id); }}
                            className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {pager.total === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-sm text-zinc-500">
                  No clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pager {...pager} testidPrefix="client-pager" />
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">
              {modal.mode === "create" ? "New client" : "Edit client"}
            </h2>
            <ClientForm
              initial={modal.row}
              contacts={contacts}
              onCancel={() => setModal(null)}
              onSubmit={(form) => {
                if (modal.mode === "create") createM.mutate(form);
                else updateM.mutate({ id: modal.row.id, data: form });
              }}
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
}
